import { switchMap } from 'rxjs/operators';
import { fetch, fetch$ } from './fetch.js';
/**
 * @internal
 */
export const isLocalEnvironment = /^localhost|127\.0\.0\.1/.test(globalThis.location.hostname);
function applyInterceptors(interceptors, input, init) {
    let req;
    if (input && init) {
        req = new Request(input, init);
    }
    else if (!init && !(input instanceof Request)) {
        req = new Request(input);
    }
    else if (input instanceof Request) {
        req = input;
    }
    else {
        throw new Error(`The input configuration is not supported`);
    }
    return interceptors.reduce((reqAcc, interceptor) => interceptor(reqAcc), req);
}
async function applyResponseInterceptors(interceptors, response) {
    for (const interceptor of interceptors) {
        const cloned = response.clone();
        // eslint-disable-next-line no-await-in-loop
        response = await interceptor(cloned);
    }
    return response.clone();
}
/**
 * Creates a new fetch function with the array of interceptors being applied to modify the behavior of the fetch
 *
 * @public
 */
export function composeFetch(...interceptors) {
    const flatten = interceptors.flat();
    return async (input, init, rejectOnCancel) => {
        const req = applyInterceptors(flatten, input, init);
        return fetch(req, undefined, rejectOnCancel);
    };
}
/**
 * Creates a new Fetch function that executes a series of interceptors over the response.
 * @public
 * @param interceptors - An array of interceptors to be applied to the response.
 * @param fetchFn - The fetch function to be executed (this can be used to use a Fetch created by {@link composeFetch}).
 * @returns A new Fetch function that executes the interceptors over the response.
 */
export function composeFetchResponse(interceptors, fetchFn = fetch) {
    return async (input, init, rejectOnCancel) => {
        const res = await fetchFn(input, init, rejectOnCancel);
        return applyResponseInterceptors(interceptors, res);
    };
}
/**
 * Creates a new fetch type function with the array of interceptors being applied to modify the behavior of the fetch
 *
 * @public
 */
export function composeFetch$(...interceptors) {
    const flatten = interceptors.flat();
    return (input, init) => {
        const req = applyInterceptors(flatten, input, init);
        return fetch$(req);
    };
}
/**
 * Creates a new Fetch function that executes a series of interceptors over the response.
 * @public
 * @param interceptors - An array of interceptors to be applied to the response.
 * @param fetchFn - The fetch function to be executed (this can be used to use a Fetch created by {@link composeFetch}).
 * @returns A new Fetch function that executes the interceptors over the response.
 */
export function composeFetch$Response(interceptors, fetchFn = fetch$) {
    return (input, init) => {
        const res = fetchFn(input, init);
        return res.pipe(switchMap(async (res) => applyResponseInterceptors(interceptors, res)));
    };
}
/**
 * Adds the credentials setting with the value same-origin to all Requests
 * @remarks During local development the credentials will be send to all domains to allow local proxy. When deploy a same-origin policy is needed
 * @public
 */
export const credentialsInterceptor = req => new Request(req, { credentials: isLocalEnvironment ? 'include' : 'same-origin' });
